// Is this signed-in uid a studio, a family's portal login, or both (#764)?
//
// The rule is the core's (core/src/identity.rs, whose header has the
// argument); this is the web's and the Cloud Functions' door to it, plus the
// one thing only JavaScript needs: reading the portal claim out of an ID
// token the browser already holds, without a network call.
//
// In short: no membership and no claim is a studio; a billing account is a
// studio whatever else the uid has; a portal signal with no billing account is
// a portal login and nothing else; and a portal signal whose account has not
// been read yet waits.

import { coreSync } from './core.js';

export const IDENTITY_ROLE = Object.freeze({
  studio: 'studio',
  both: 'both',
  portal: 'portal',
  pending: 'pending',
});

/** What is known of `users/{uid}/billing/account`. */
export const BILLING_KNOWN = Object.freeze({
  present: 'present',
  absent: 'absent',
  unknown: 'unknown',
});

/**
 * `studio`, `both`, `portal` or `pending`.
 *
 * `memberships` is how many `portalMembers` documents name the uid — any
 * number, zero included (a parent may be in several studios, and in several
 * families within one). A client passes zero and its claim; the server passes
 * the count it read and the claim when it has the user record.
 */
export function identityRole({ memberships = 0, portalClaim: claim = false, billing = BILLING_KNOWN.unknown } = {}) {
  const count = Number.isFinite(memberships) && memberships > 0 ? Math.min(Math.floor(memberships), 0xffffffff) : 0;
  return coreSync().identityRole(count, claim === true, String(billing || BILLING_KNOWN.unknown));
}

/** Whether a role gets a studio: `studio` and `both`. */
export function isStudioRole(role) {
  return coreSync().identityIsStudio(String(role || ''));
}

/** The custom claim's name. */
export function portalClaim() {
  return coreSync().portalClaim();
}

/** Where the web app sends a portal-only uid. */
export function portalPath() {
  return coreSync().portalPath();
}

/** The phones' sentence, here for the vectors and for anything that shows it. */
export function portalOnlyPhoneMessage(domain) {
  return coreSync().portalOnlyPhoneMessage(String(domain || ''));
}

/** Whether a token's claims carry the portal claim. Only the boolean `true` counts. */
export function hasPortalClaim(claims) {
  return claims != null && typeof claims === 'object' && claims[portalClaim()] === true;
}

// The claims an ID token carries, read from the token itself, or null when
// it is not one. No signature check: this is the browser reading its own
// token to decide which screen to draw, and every authority (the rules, the
// callables) verifies the token for itself. Expired tokens are read too —
// the claim does not change when a token is refreshed, and a device offline
// with a stale token must still decide the same way.
export function claimsOfIdToken(token) {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes));
    return claims && typeof claims === 'object' && !Array.isArray(claims) ? claims : null;
  } catch {
    return null;
  }
}
